import { computed, ref, watchEffect } from 'vue'
import { isArray, isNumber } from '@bigin/utils'
import { useNamespace } from '@bigin/hooks'

import type { SpaceProps } from './space'

import type { CSSProperties, StyleValue } from 'vue'

const SIZE_MAP = {
  xxsmall: 4,
  xsmall: 8,
  small: 16,
  default: 24,
  large: 32,
  xlarge: 40,
  '': 0,
}

export function useSpace(props: SpaceProps) {
  const ns = useNamespace('space')

  const classes = computed(() => [
    ns.b(),
    ns.m(
      props.vertical ?? props.direction === 'vertical'
        ? 'vertical'
        : 'horizontal'
    ),
    props.class,
  ])

  const horizontalSize = ref(0)
  const verticalSize = ref(0)

  const containerStyle = computed<StyleValue>(() => {
    const wrapKls: CSSProperties =
      props.wrap || props.fill
        ? {
            flexWrap: 'wrap',
            marginBottom: `-${verticalSize.value}px`,
            marginRight: `-${horizontalSize.value}px`,
          }
        : {}
    const alignment: CSSProperties = {
      alignItems: props.alignment,
    }
    return [wrapKls, alignment, props.style]
  })

  const itemStyle = computed<StyleValue>(() => {
    const itemBaseStyle: CSSProperties = {
      paddingBottom: `${verticalSize.value}px`,
      paddingRight: `${horizontalSize.value}px`,
    }

    const fillStyle: CSSProperties = props.fill
      ? { flexGrow: 1, minWidth: `${props.fillRatio}%` }
      : {}

    return [itemBaseStyle, fillStyle]
  })

  watchEffect(() => {
    const {
      size = 'small',
      gutter,
      wrap,
      direction: dir,
      vertical,
      fill,
    } = props

    // when the specified size have been given
    if (isArray(size)) {
      const [h = 0, v = 0] = size
      horizontalSize.value = h * 4
      verticalSize.value = v * 4
    } else {
      let val: number
      if (isNumber(size)) {
        val = size * 4
      } else {
        val = SIZE_MAP[size] || SIZE_MAP.small
      }

      if (gutter > -1) {
        val = gutter * 4
      }

      if ((wrap || fill) && dir === 'horizontal' && !vertical) {
        horizontalSize.value = verticalSize.value = val
      } else {
        if (dir === 'horizontal' && !vertical) {
          horizontalSize.value = val
          verticalSize.value = 0
        } else {
          verticalSize.value = val
          horizontalSize.value = 0
        }
      }
    }
  })

  return {
    classes,
    containerStyle,
    itemStyle,
  }
}
